import React, { useState } from 'react';
import { Alert, Button, Card, Container, Form, Spinner } from 'react-bootstrap';
import { authAPI } from '../../api/wbuoaApi';
import { useAuth } from '../../context/AuthContext';
import { getApiErrorMessage } from '../../utils/apiError';

const initialForm = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
};

const UpdatePassword = () => {
  const { user } = useAuth();
  const profile = user?.data || user || {};
  const [formData, setFormData] = useState(initialForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleChange = ({ target: { name, value } }) => {
    setFormData((current) => ({ ...current, [name]: value }));
    setFieldErrors((current) => ({ ...current, [name]: undefined }));
    setError('');
    setSuccess('');
  };

  const validate = () => {
    const errors = {};

    if (!formData.currentPassword) errors.currentPassword = 'Enter your current password.';
    if (!formData.newPassword) {
      errors.newPassword = 'Enter a new password.';
    } else if (formData.newPassword.length < 6) {
      errors.newPassword = 'The new password must contain at least 6 characters.';
    } else if (formData.newPassword === formData.currentPassword) {
      errors.newPassword = 'Choose a password different from your current password.';
    }
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Enter the new password again.';
    } else if (formData.confirmPassword !== formData.newPassword) {
      errors.confirmPassword = 'The new passwords do not match.';
    }

    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    const errors = validate();
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }

    if (!profile.email) {
      setError('Your account email is unavailable. Please log out and sign in again.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await authAPI.updatePassword(
        profile.email,
        formData.currentPassword,
        formData.newPassword
      );
      setFormData(initialForm);
      setFieldErrors({});
      setSuccess(response?.message || 'Password updated successfully.');
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Unable to update the password. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container className="py-4" style={{ maxWidth: '680px' }}>
      <Card className="shadow-sm">
        <Card.Header className="bg-success text-white">
          <h2 className="h4 mb-1">Update Password</h2>
          <p className="mb-0 small">Change the password for {profile.email}.</p>
        </Card.Header>
        <Card.Body className="p-4">
          {error && <Alert variant="danger" role="alert">{error}</Alert>}
          {success && <Alert variant="success" role="status">{success}</Alert>}

          <Form onSubmit={handleSubmit} noValidate>
            <Form.Group className="mb-3" controlId="current-password">
              <Form.Label style={{ color: '#1a4a2f' }}>Current password</Form.Label>
              <Form.Control
                type="password"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleChange}
                autoComplete="current-password"
                isInvalid={Boolean(fieldErrors.currentPassword)}
                disabled={submitting}
                autoFocus
              />
              <Form.Control.Feedback type="invalid">
                {fieldErrors.currentPassword}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="new-password">
              <Form.Label style={{ color: '#1a4a2f' }}>New password</Form.Label>
              <Form.Control
                type="password"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                autoComplete="new-password"
                isInvalid={Boolean(fieldErrors.newPassword)}
                disabled={submitting}
              />
              <Form.Text>Use at least 6 characters.</Form.Text>
              <Form.Control.Feedback type="invalid">
                {fieldErrors.newPassword}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-4" controlId="confirm-password">
              <Form.Label style={{ color: '#1a4a2f' }}>Confirm new password</Form.Label>
              <Form.Control
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                autoComplete="new-password"
                isInvalid={Boolean(fieldErrors.confirmPassword)}
                disabled={submitting}
              />
              <Form.Control.Feedback type="invalid">
                {fieldErrors.confirmPassword}
              </Form.Control.Feedback>
            </Form.Group>

            <Button type="submit" variant="success" disabled={submitting}>
              {submitting && <Spinner animation="border" size="sm" className="me-2" />}
              {submitting ? 'Updating password...' : 'Update Password'}
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default UpdatePassword;

